export interface ConfiguredTreatmentDisplayItem {
  name: string;
  method: string;
  dose: string | number;
}

const getValueByPath = (value: unknown, path: string): unknown => {
  if (!path) {
    return undefined;
  }

  const segments = path.split(".");
  let current: unknown = value;

  for (const segment of segments) {
    if (!hasObjectShape(current)) {
      return undefined;
    }

    current = current[segment];
  }

  return current;
};

const getFirstStringByPaths = (
  value: unknown,
  paths: string[],
): string | null => {
  for (const path of paths) {
    const normalized = normalizeString(getValueByPath(value, path));
    if (normalized) {
      return normalized;
    }
  }

  return null;
};

const getFirstDoseByPaths = (
  value: unknown,
  paths: string[],
): string | number | null => {
  for (const path of paths) {
    const normalized = normalizeDose(getValueByPath(value, path));
    if (normalized !== null) {
      return normalized;
    }
  }

  return null;
};

const BATCH_FIELDS = [
  "batch",
  "batchNo",
  "batch_no",
  "batchNumber",
  "batch_number",
  "batchId",
  "batch_id",
  "batchCode",
  "batch_code",
  "batchLabel",
  "batch_label",
  "batchName",
  "batch_name",
  "lot",
  "lotNo",
  "lot_no",
  "lotNumber",
  "lot_number",
  "lotId",
  "lot_id",
  "lotCode",
  "lot_code",
  "serial",
  "serialNumber",
  "serial_number",
  "catalogNumber",
  "catalog_number",
];

const BATCH_PREFIXES = [
  "",
  "vaccine.",
  "material.",
  "challengeMaterial.",
  "product.",
  "ivp.",
];

const BATCH_PATHS = BATCH_PREFIXES.flatMap((prefix) =>
  BATCH_FIELDS.map((field) => `${prefix}${field}`),
);

const collectConfiguredItems = (
  configuration: unknown,
  listPaths: string[],
  singlePaths: string[],
): unknown[] => {
  if (!hasObjectShape(configuration)) {
    return [];
  }

  const configuredItems = listPaths
    .flatMap((path) => {
      const value = getValueByPath(configuration, path);
      return Array.isArray(value) ? value : [];
    })
    .filter((value) => value !== null && value !== undefined);

  if (configuredItems.length > 0) {
    return configuredItems;
  }

  for (const path of singlePaths) {
    const value = getValueByPath(configuration, path);

    if (Array.isArray(value) && value.length > 0) {
      return value.filter((item) => item !== null && item !== undefined);
    }

    if (value !== null && value !== undefined) {
      return [value];
    }
  }

  return [];
};

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeDose = (value: unknown): string | number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  return null;
};

const hasObjectShape = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

export const getConfigurationBlindingCode = (
  configuration: unknown,
): string | null => {
  if (!hasObjectShape(configuration)) {
    return null;
  }

  return getFirstStringByPaths(configuration, [
    "blindingCode",
    "blinding_code",
    "blindedCode",
    "blinded_code",
    "blindCode",
    "blind_code",
    "blinding",
    "blinded",
    "productBlindingCode",
    "product_blinding_code",
  ]);
};

const getVaccinationProductName = (
  configuredVaccine: unknown,
): string | null => {
  if (typeof configuredVaccine === "string") {
    return normalizeString(configuredVaccine);
  }

  if (!hasObjectShape(configuredVaccine)) {
    return null;
  }

  return getFirstStringByPaths(configuredVaccine, [
    "vaccine.name",
    "vaccine.label",
    "vaccine.title",
    "vaccineName",
    "ivp.name",
    "ivp.label",
    "ivp.title",
    "ivpName",
    "product.name",
    "product.label",
    "product.title",
    "productName",
    "name",
    "title",
    "displayName",
    "tradeName",
    "brandName",
  ]);
};

const getChallengeProductName = (
  configuredMaterial: unknown,
): string | null => {
  if (typeof configuredMaterial === "string") {
    return normalizeString(configuredMaterial);
  }

  if (!hasObjectShape(configuredMaterial)) {
    return null;
  }

  return getFirstStringByPaths(configuredMaterial, [
    "material.name",
    "material.label",
    "material.title",
    "materialName",
    "challengeMaterial.name",
    "challengeMaterial.label",
    "challengeMaterial.title",
    "challengeMaterialName",
    "product.name",
    "product.label",
    "product.title",
    "productName",
    "name",
    "title",
    "displayName",
    "tradeName",
    "brandName",
  ]);
};

const getMethodName = (configuredItem: unknown): string | null => {
  if (typeof configuredItem === "string") {
    return normalizeString(configuredItem);
  }

  if (!hasObjectShape(configuredItem)) {
    return null;
  }

  return getFirstStringByPaths(configuredItem, [
    "method.name",
    "method.label",
    "method.title",
    "methodName",
    "method",
    "applicationMethod.name",
    "applicationMethod.label",
    "applicationMethod.title",
    "applicationMethodName",
    "applicationMethod",
    "administrationMethod.name",
    "administrationMethod.label",
    "administrationMethodName",
    "route.name",
    "route.label",
    "route.title",
  ]);
};

const getDose = (configuredItem: unknown): string | number | null => {
  if (
    typeof configuredItem === "number" ||
    typeof configuredItem === "string"
  ) {
    return normalizeDose(configuredItem);
  }

  if (!hasObjectShape(configuredItem)) {
    return null;
  }

  return getFirstDoseByPaths(configuredItem, [
    "dose",
    "doseMl",
    "doseML",
    "dosage",
    "amount",
    "amountMl",
    "amountML",
    "ml",
    "volume",
    "quantity",
    "dose.value",
    "amount.value",
    "volume.value",
  ]);
};

const getBatchValue = (configuredItem: unknown): string | null => {
  if (typeof configuredItem === "string") {
    return normalizeString(configuredItem);
  }

  if (!hasObjectShape(configuredItem)) {
    return null;
  }

  return getFirstStringByPaths(configuredItem, BATCH_PATHS);
};

const buildProductLabel = (
  name: string | null,
  batch: string | null,
): string => {
  if (name && batch) {
    return `${name} (${batch})`;
  }

  if (name) {
    return name;
  }

  if (batch) {
    return `Batch ${batch}`;
  }

  return "Unknown";
};

const mapConfiguredItems = (
  configuredItems: unknown[],
  getName: (configuredItem: unknown) => string | null,
  blindingCode: string | null,
  defaultMethod: string,
  configuration: unknown,
): ConfiguredTreatmentDisplayItem[] => {
  const fallbackName = getName(configuration);
  const fallbackBatch = getBatchValue(configuration);
  const fallbackMethod = getMethodName(configuration);
  const fallbackDose = getDose(configuration);
  const hasFallbackProductIdentity = !!(fallbackName || fallbackBatch);

  if (!Array.isArray(configuredItems) || configuredItems.length === 0) {
    if (!blindingCode && !hasFallbackProductIdentity) {
      return [];
    }

    return [
      {
        name: blindingCode ?? buildProductLabel(fallbackName, fallbackBatch),
        method: fallbackMethod ?? defaultMethod,
        dose: fallbackDose ?? "N/A",
      },
    ];
  }

  return configuredItems.map((configuredItem) => {
    const resolvedName =
      blindingCode ??
      buildProductLabel(
        getName(configuredItem) ?? fallbackName,
        getBatchValue(configuredItem) ?? fallbackBatch,
      );
    const resolvedMethod =
      getMethodName(configuredItem) ?? fallbackMethod ?? defaultMethod;

    return {
      name: resolvedName,
      method: resolvedMethod,
      dose: getDose(configuredItem) ?? fallbackDose ?? "N/A",
    };
  });
};

export const getVaccinationProductDisplayItems = (
  configuration: unknown,
  defaultMethod = "Unknown",
): ConfiguredTreatmentDisplayItem[] => {
  if (!hasObjectShape(configuration)) {
    return [];
  }

  const configuredVaccines = collectConfiguredItems(
    configuration,
    [
      "vaccines",
      "configuredVaccines",
      "vaccineItems",
      "ivps",
      "products",
      "treatments",
    ],
    [
      "vaccine",
      "configuredVaccine",
      "vaccineItem",
      "ivp",
      "product",
      "treatment",
    ],
  );

  return mapConfiguredItems(
    configuredVaccines,
    getVaccinationProductName,
    getConfigurationBlindingCode(configuration),
    defaultMethod,
    configuration,
  );
};

export const getChallengeMaterialDisplayItems = (
  configuration: unknown,
  defaultMethod = "Unknown",
): ConfiguredTreatmentDisplayItem[] => {
  if (!hasObjectShape(configuration)) {
    return [];
  }

  const configuredMaterials = collectConfiguredItems(
    configuration,
    [
      "challengeMaterials",
      "configuredChallengeMaterials",
      "materials",
      "products",
      "treatments",
    ],
    [
      "challengeMaterial",
      "configuredChallengeMaterial",
      "material",
      "product",
      "treatment",
    ],
  );

  return mapConfiguredItems(
    configuredMaterials,
    getChallengeProductName,
    getConfigurationBlindingCode(configuration),
    defaultMethod,
    configuration,
  );
};

export const isLikelyBathVaccinationConfiguration = (
  configuration: unknown,
): boolean => {
  if (!hasObjectShape(configuration)) {
    return false;
  }

  if (
    configuration.isSimplifiedBath === true ||
    configuration.isStandardBath === true
  ) {
    return true;
  }

  if (
    hasObjectShape(configuration.tank) ||
    hasObjectShape(configuration.bathProcess) ||
    configuration.challengeDuration !== undefined ||
    configuration.requiresWeighing !== undefined ||
    configuration.duration !== undefined ||
    configuration.bathDuration !== undefined ||
    configuration.vaccinationDuration !== undefined
  ) {
    return true;
  }

  const vaccines = Array.isArray(configuration.vaccines)
    ? configuration.vaccines
    : [];

  return vaccines.some((configuredVaccine) => {
    if (!hasObjectShape(configuredVaccine)) {
      return false;
    }

    const methodName = normalizeString(
      getValueByPath(configuredVaccine, "method.name") ??
        getValueByPath(configuredVaccine, "method"),
    )?.toLowerCase();
    const methodKey = normalizeString(
      getValueByPath(configuredVaccine, "method.key"),
    )?.toLowerCase();
    return methodName === "bath" || methodKey === "bath";
  });
};
